import React, { useState } from "react";
import { Pencil, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls } from "./EaUi";
import { MONTHS_IT, fmtEur, fmtSignedPct } from "./eaFormat";
import { NAME, errMsg } from "./eaFixUtil";

const btn = "h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2 disabled:opacity-50";
const money = (t) => {
  let s = String(t ?? "").replace(/[€\s]/g, "");
  if (!s) return null;
  s = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, "") : s;
  const n = Number(s);
  return Number.isNaN(n) ? NaN : n;
};
const sgn = (v) => (v > 0 ? "+" : v < 0 ? "−" : "");
const Delta = ({ v, base }) => (v == null || !base ? <span className="text-slate-400">—</span>
  : <span className={v > 0 ? "text-red-600" : "text-green-600"}>{sgn(v)}{fmtEur(Math.abs(v))} <span className="text-xs">({fmtSignedPct((v / base) * 100)})</span></span>);

export function BudgetYtd({ b, testid }) {
  if (b.budget_ytd_eur == null) return <span className="text-slate-400" data-testid={testid}>Budget YTD non disponibile</span>;
  const d = b.invoiced_eur - b.budget_ytd_eur;
  return <span className={`font-semibold ${d > 0 ? "text-red-600" : "text-green-600"}`} data-testid={testid}>YTD ({b.months_invoiced} mesi fatturati): {sgn(d)}{fmtEur(Math.abs(d))} vs Budget YTD {fmtEur(b.budget_ytd_eur)} ({fmtSignedPct((d / b.budget_ytd_eur) * 100)})</span>;
}

export function EaBudgetMonthly({ b, year, params, filtered, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [ed, setEd] = useState({});
  const [busy, setBusy] = useState(false);
  let cb = 0, cf = 0;
  const rows = b.monthly.map((m) => {
    const fc = m.dynamic_eur != null ? (m.invoiced_eur || 0) + m.dynamic_eur : m.invoiced_eur;
    cb += m.budget_eur || 0; cf += fc || 0;
    return { ...m, fc, cb, cf };
  });
  const totB = cb, totF = cf;
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setEdit(false); setEd({}); onSaved(); } catch (e) { toast.error(errMsg(e, "Salvataggio non riuscito")); }
    setBusy(false);
  };
  const save = () => {
    const months = Object.fromEntries(Object.entries(ed).map(([m, v]) => [m, money(v)]));
    if (Object.values(months).some((v) => Number.isNaN(v) || (v != null && v < 0))) return toast.error("Importi non validi");
    if (!Object.keys(months).length) return setEdit(false);
    return call(() => api.put("/ea/budget/monthly", { year, commodity: b.commodity, months }, { params }), "Budget mensile salvato: il budget annuale è la somma dei mesi");
  };
  const reset = () => call(() => api.delete("/ea/budget/monthly", { params: { ...params, year, commodity: b.commodity } }), "Ripartizione mensile automatica ripristinata");
  return (
    <div className="card-elevated overflow-x-auto" data-testid={`ea-budget-monthly-${b.commodity}`}>
      <div className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-slate-900" data-pdf-title>Budget mensile vs Forecast · {NAME[b.commodity]} {year}</div>
          <div className="text-xs text-slate-500 max-w-3xl" data-testid={`ea-budget-monthly-mode-${b.commodity}`}>
            {b.budget_monthly_mode === "edited" ? "Ripartizione mensile modificata: il profilo salvato si riscala se cambia il budget annuale." : "Ripartizione automatica: budget annuale distribuito in proporzione al forecast mensile del file (fatturato + previsionale), quindi lo scostamento % è uguale nei mesi fatturati finché non modifichi i mesi."}
            {" "}Modificando un mese il budget annuale diventa la somma dei mesi. Forecast = fatturato o Forecast Dinamico per i mesi non fatturati.
          </div>
        </div>
        <div className="flex gap-2">
          {!edit && filtered && <span className="text-xs text-slate-500 self-center" data-testid={`ea-bm-filtered-${b.commodity}`}>Per modificare i mesi seleziona tutti i punti</span>}
          {!edit && !filtered && <button onClick={() => setEdit(true)} className={btn} data-testid={`ea-bm-edit-${b.commodity}`}><Pencil size={14} /> Modifica mesi</button>}
          {edit && <button onClick={save} disabled={busy} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid={`ea-bm-save-${b.commodity}`}><Save size={15} /> Salva</button>}
          {edit && <button onClick={() => { setEdit(false); setEd({}); }} className={btn} data-testid={`ea-bm-cancel-${b.commodity}`}><X size={14} /> Annulla</button>}
          {b.budget_monthly_mode === "edited" && <button onClick={reset} disabled={busy} className={btn} data-testid={`ea-bm-reset-${b.commodity}`}><RotateCcw size={14} /> Ripartizione automatica</button>}
        </div>
      </div>
      <table className="w-full text-sm font-mono-nums">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
          <tr>{["Mese", "Budget", "Forecast", "Δ vs budget", "Budget progressivo", "Forecast progressivo", "Δ progressivo"].map((h, i) => <th key={h} className={`px-3 py-2 ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month} className="border-t border-slate-100" data-testid={`ea-bm-row-${b.commodity}-${r.month}`}>
              <td className="px-3 py-1.5 font-sans">{MONTHS_IT[r.month - 1]} <span className="text-xs text-slate-400">{r.invoiced_eur ? "fatturato" : r.dynamic_eur != null ? "dinamico" : ""}</span></td>
              <td className="px-3 py-1.5 text-right">{edit ? <input value={ed[r.month] ?? (r.budget_eur != null ? String(Math.round(r.budget_eur)) : "")} onChange={(e) => setEd({ ...ed, [r.month]: e.target.value })} className={`${inputCls} w-32 text-right`} data-testid={`ea-bm-input-${b.commodity}-${r.month}`} /> : fmtEur(r.budget_eur)}</td>
              <td className="px-3 py-1.5 text-right">{fmtEur(r.fc)}</td>
              <td className="px-3 py-1.5 text-right"><Delta v={r.fc != null && r.budget_eur != null ? r.fc - r.budget_eur : null} base={r.budget_eur} /></td>
              <td className="px-3 py-1.5 text-right">{fmtEur(r.cb)}</td>
              <td className="px-3 py-1.5 text-right">{fmtEur(r.cf)}</td>
              <td className="px-3 py-1.5 text-right"><Delta v={r.cf - r.cb} base={r.cb} /></td>
            </tr>
          ))}
          <tr className="border-t-2 border-slate-400 bg-slate-50 font-semibold text-slate-900" data-pdf-total data-testid={`ea-bm-total-${b.commodity}`}>
            <td className="px-3 py-2 font-sans">Totale anno</td><td className="px-3 py-2 text-right">{fmtEur(totB)}</td><td className="px-3 py-2 text-right">{fmtEur(totF)}</td>
            <td className="px-3 py-2 text-right"><Delta v={totF - totB} base={totB} /></td><td colSpan={3} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}
